import React, { useState } from "react";
import { Link } from "react-router-dom";
import PricingCard from "../components/PricingCard";
import * as api from "../services/api";
import { trackAuthed } from "../services/events";

const faqs = [
  { q: "How does billing work?", a: "You are billed monthly on the date you subscribe. Cancel any time — no long-term contracts. Your card is processed securely through Stripe." },
  { q: "Is there a free trial?", a: "The Free plan gives you access to tools with 3 runs per day — permanently, not just for a trial period. Upgrade when you need more." },
  { q: "Can I change plans later?", a: "Yes. Upgrade or downgrade at any time from your dashboard. Changes take effect immediately with prorated billing." },
  { q: "Do you offer refunds?", a: "We offer a 14-day money-back guarantee on all paid plans, no questions asked. Email [email protected] and we will process it within 24 hours." },
  { q: "Is my payment information secure?", a: "Yes. All payments are processed exclusively by Stripe — PCI DSS Level 1 certified. We never receive, store, or log your card data." },
  { q: "What happens to my data if I cancel?", a: "Your data remains intact on the Free plan. You can export or delete it at any time from Settings. We do not delete your data automatically when you downgrade." },
  { q: "Do you offer consulting as a paid service?", a: "Yes. Our consulting practice runs separately from the SaaS subscription. Engagements start with a free 30-minute scoping call. See the Consulting page for details." },
];

const comparisonRows = [
  { feature: "Tool runs per day", free: "3", pro: "Unlimited", enterprise: "Unlimited" },
  { feature: "Tools included", free: "10 of 12", pro: "All 12", enterprise: "All 12 + custom" },
  { feature: "Export results", free: "—", pro: "✓", enterprise: "✓" },
  { feature: "API access", free: "—", pro: "✓", enterprise: "✓" },
  { feature: "Saved run history", free: "7 days", pro: "Unlimited", enterprise: "Unlimited" },
  { feature: "Budget Planner", free: "Basic modes", pro: "All 11 modes", enterprise: "All 11 modes" },
  { feature: "Household sync", free: "—", pro: "✓", enterprise: "✓" },
  { feature: "Support", free: "Community", pro: "Priority email", enterprise: "Dedicated" },
  { feature: "SLA guarantee", free: "—", pro: "—", enterprise: "✓" },
  { feature: "Team management", free: "—", pro: "—", enterprise: "✓" },
  { feature: "White-label option", free: "—", pro: "—", enterprise: "✓" },
  { feature: "Custom tool development", free: "—", pro: "—", enterprise: "✓" },
];

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSelect = async (plan: string) => {
    if (plan === "free") {
      trackAuthed("pricing_select_free", { plan });
      window.location.href = "/register";
      return;
    }
    try {
      trackAuthed("checkout_started", { plan });
      const data = await api.createCheckout(plan);
      window.location.href = data.url;
    } catch {
      trackAuthed("checkout_failed_redirect_register", { plan });
      window.location.href = "/register";
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* ── Header ── */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-indigo-300 font-semibold uppercase tracking-widest text-sm mb-5">Pricing</p>
          <h1 className="text-5xl font-extrabold mb-4 leading-tight">Simple, transparent pricing</h1>
          <p className="text-xl text-slate-300">Start free. No credit card required. Upgrade when you need more power.</p>
        </div>
      </section>

      {/* ── Trust badges ── */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-5 flex flex-wrap justify-center gap-5 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">✅ No credit card to start</span>
          <span className="flex items-center gap-1.5">✅ Cancel any time</span>
          <span className="flex items-center gap-1.5">✅ 14-day money-back guarantee</span>
          <span className="flex items-center gap-1.5">🔒 Payments via Stripe · PCI DSS L1</span>
        </div>
      </section>

      {/* ── Cards ── */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          <PricingCard name="Free" price={0} features={["3 tool runs per day", "10 free tools", "Basic results", "Community support"]} isPopular={false} ctaText="Get Started Free" onSelect={() => handleSelect("free")} />
          <PricingCard name="Pro" price={19} features={["Unlimited tool runs", "All 12 tools", "Export results", "Priority support", "API access"]} isPopular={true} ctaText="Go Pro" onSelect={() => handleSelect("pro")} />
          <PricingCard name="Enterprise" price={49} features={["Everything in Pro", "Custom tools", "Dedicated support", "SLA guarantee", "Team management", "White-label option"]} isPopular={false} ctaText="Contact Sales" onSelect={() => handleSelect("enterprise")} />
        </div>
      </section>

      {/* ── Comparison table ── */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Full plan comparison</h2>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-4 font-semibold text-gray-500 w-1/2">Feature</th>
                  <th className="px-5 py-4 font-bold text-gray-700 text-center">Free</th>
                  <th className="px-5 py-4 font-bold text-indigo-600 text-center bg-indigo-50/60">Pro</th>
                  <th className="px-5 py-4 font-bold text-gray-700 text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-gray-50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                    <td className="px-5 py-3.5 text-gray-700 font-medium">{row.feature}</td>
                    <td className="px-5 py-3.5 text-gray-500 text-center">{row.free}</td>
                    <td className="px-5 py-3.5 text-indigo-700 font-semibold text-center bg-indigo-50/40">{row.pro}</td>
                    <td className="px-5 py-3.5 text-gray-700 text-center">{row.enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Consulting CTA ── */}
      <section className="py-14 bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div>
            <p className="text-indigo-200 text-sm font-semibold uppercase tracking-widest mb-2">Need something custom?</p>
            <h2 className="text-2xl font-extrabold mb-1">Our consulting practice works with any team size.</h2>
            <p className="text-indigo-200 text-sm">Structured engagements in Cloud, CMS, Analytics, Automation & Atlassian. Starting with a free scoping call.</p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3">
            <Link to="/consulting" className="inline-block bg-white text-indigo-700 font-bold px-7 py-3 rounded-xl hover:bg-indigo-50 transition whitespace-nowrap shadow">
              Consulting Services →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <button
                  className="w-full text-left px-6 py-4 font-semibold text-gray-800 flex justify-between items-center hover:bg-gray-50 transition text-sm"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {faq.q}
                  <span className="text-indigo-500 text-xl font-light shrink-0 ml-4">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-100 pt-3">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom trust strip ── */}
      <section className="py-10 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="flex flex-wrap gap-5 justify-center sm:justify-start">
            <span className="flex items-center gap-1.5">🔒 TLS 1.3 encrypted</span>
            <span className="flex items-center gap-1.5">💳 Stripe · PCI DSS L1</span>
            <span className="flex items-center gap-1.5">🗑️ Delete your data anytime</span>
          </div>
          <div className="flex gap-4">
            <Link to="/trust" className="text-indigo-600 font-semibold hover:underline underline-offset-2">Trust & Security →</Link>
            <a href="/privacy" className="hover:text-gray-800 hover:underline underline-offset-2">Privacy Policy</a>
          </div>
        </div>
      </section>

    </div>
  );
}
